const SCALE_SPEED_PCT_PER_SECOND = {
  very_slow: 1,
  slow: 2,
  medium: 4,
  fast: 7,
  very_fast: 10,
}

const POSITION_SPEED_UNITS_PER_SECOND = {
  very_slow: 3,
  slow: 6,
  medium: 12,
  fast: 20,
  very_fast: 32,
}

const ROTATION_SPEED_DEG_PER_SECOND = {
  very_slow: 2,
  slow: 4,
  medium: 8,
  fast: 14,
  very_fast: 24,
}

const INTENSITY_MULTIPLIERS = {
  subtle: 0.7,
  normal: 1,
  strong: 1.35,
}

const DEFAULT_SAFE_SCALE_FLOOR_PCT = 100

const finite = (value, fallback = 0) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : fallback
}

const normalizeProfile = (profile) =>
  Object.prototype.hasOwnProperty.call(SCALE_SPEED_PCT_PER_SECOND, profile)
    ? profile
    : 'medium'

const normalizeIntensity = (intensity) =>
  Object.prototype.hasOwnProperty.call(INTENSITY_MULTIPLIERS, intensity)
    ? intensity
    : 'normal'

const framesToSeconds = (frameSpan, fps) => {
  const frames = Math.max(1, Math.round(finite(frameSpan, 1)))
  const safeFps = Math.max(1, finite(fps, 24))
  return frames / safeFps
}

const makeScalePct = (x, y, z) => ({
  x: finite(x, DEFAULT_SAFE_SCALE_FLOOR_PCT),
  y: finite(y, DEFAULT_SAFE_SCALE_FLOOR_PCT),
  z: finite(z, DEFAULT_SAFE_SCALE_FLOOR_PCT),
})

const makePosition = (x, y, z) => ({
  x: finite(x, 0),
  y: finite(y, 0),
  z: finite(z, 0),
})

const makeRotationDeg = (x, y, z) => ({
  x: finite(x, 0),
  y: finite(y, 0),
  z: finite(z, 0),
})

const normalizeTransform = (transform = {}) => ({
  position: makePosition(
    transform?.position?.x,
    transform?.position?.y,
    transform?.position?.z
  ),
  scalePct: makeScalePct(
    transform?.scalePct?.x,
    transform?.scalePct?.y,
    transform?.scalePct?.z
  ),
  rotationDeg: makeRotationDeg(
    transform?.rotationDeg?.x,
    transform?.rotationDeg?.y,
    transform?.rotationDeg?.z
  ),
})

const pickRotationAxis = (transformMode = '2d') =>
  transformMode === '3d' ? 'y' : 'z'

export const TRANSFORM_SPEED_CONFIG = {
  scalePctPerSecond: SCALE_SPEED_PCT_PER_SECOND,
  positionUnitsPerSecond: POSITION_SPEED_UNITS_PER_SECOND,
  rotationDegPerSecond: ROTATION_SPEED_DEG_PER_SECOND,
  intensityMultipliers: INTENSITY_MULTIPLIERS,
  safeScaleFloorPct: DEFAULT_SAFE_SCALE_FLOOR_PCT,
}

export const resolveTransformSpeed = ({
  intent = 'hold',
  speedProfile = 'medium',
  intensity = 'normal',
  frameSpan = 1,
  fps = 24,
  currentTransform = null,
  transformMode = '2d',
  framingPolicy = 'center_safe',
  allowBorderExposure = false,
  safeScaleFloorPct = DEFAULT_SAFE_SCALE_FLOOR_PCT,
  allowRotation = true,
} = {}) => {
  const profile = normalizeProfile(speedProfile)
  const intensityKey = normalizeIntensity(intensity)
  const intensityScale = INTENSITY_MULTIPLIERS[intensityKey]
  const seconds = framesToSeconds(frameSpan, fps)
  const baseTransform = normalizeTransform(currentTransform || {})
  const safeScaleFloor = finite(safeScaleFloorPct, DEFAULT_SAFE_SCALE_FLOOR_PCT)
  const rotationAxis = pickRotationAxis(transformMode)

  const scalePerSecond =
    SCALE_SPEED_PCT_PER_SECOND[profile] * intensityScale
  const positionPerSecond =
    POSITION_SPEED_UNITS_PER_SECOND[profile] * intensityScale
  const rotationPerSecond =
    ROTATION_SPEED_DEG_PER_SECOND[profile] * intensityScale

  const result = {
    intent,
    speedProfile: profile,
    intensity: intensityKey,
    frameSpan: Math.max(1, Math.round(finite(frameSpan, 1))),
    fps: Math.max(1, Math.round(finite(fps, 24))),
    seconds,
    perSecond: {
      scalePct: scalePerSecond,
      positionUnits: positionPerSecond,
      rotationDeg: rotationPerSecond,
    },
    totalDelta: {
      scalePct: 0,
      position: { x: 0, y: 0, z: 0 },
      rotationDeg: { x: 0, y: 0, z: 0 },
    },
    startTransform: baseTransform,
    suggestedEndTransform: normalizeTransform(baseTransform),
    clamped: false,
    noOp: false,
    reason: null,
  }

  if (intent === 'hold') {
    result.noOp = true
    result.reason = 'hold_intent'
    return result
  }

  const scaleDelta = scalePerSecond * seconds
  const positionDelta = positionPerSecond * seconds
  const rotationDelta = rotationPerSecond * seconds

  if (intent === 'zoom_in' || intent === 'zoom_out') {
    const sign = intent === 'zoom_in' ? 1 : -1
    const nextX = baseTransform.scalePct.x + sign * scaleDelta
    const nextY = baseTransform.scalePct.y + sign * scaleDelta
    const nextZ =
      transformMode === '3d'
        ? baseTransform.scalePct.z + sign * scaleDelta
        : baseTransform.scalePct.z

    let endX = nextX
    let endY = nextY
    let endZ = nextZ

    if (
      intent === 'zoom_out' &&
      framingPolicy === 'center_safe' &&
      !allowBorderExposure
    ) {
      endX = Math.max(safeScaleFloor, nextX)
      endY = Math.max(safeScaleFloor, nextY)
      if (transformMode === '3d') endZ = Math.max(safeScaleFloor, nextZ)
      if (endX !== nextX || endY !== nextY || endZ !== nextZ) {
        result.clamped = true
        result.reason = 'center_safe_scale_floor'
      }
    }

    result.totalDelta.scalePct = endX - baseTransform.scalePct.x
    result.suggestedEndTransform.scalePct = makeScalePct(endX, endY, endZ)
    if (result.totalDelta.scalePct === 0) result.noOp = true
    return result
  }

  if (
    intent === 'pan_left' ||
    intent === 'pan_right' ||
    intent === 'drift_left' ||
    intent === 'drift_right'
  ) {
    const sign = intent.includes('left') ? -1 : 1
    const delta = sign * positionDelta
    result.totalDelta.position.x = delta
    result.suggestedEndTransform.position = makePosition(
      baseTransform.position.x + delta,
      baseTransform.position.y,
      baseTransform.position.z
    )
    return result
  }

  if (
    intent === 'pan_up' ||
    intent === 'pan_down' ||
    intent === 'drift_up' ||
    intent === 'drift_down'
  ) {
    const sign = intent.includes('up') ? -1 : 1
    const delta = sign * positionDelta
    result.totalDelta.position.y = delta
    result.suggestedEndTransform.position = makePosition(
      baseTransform.position.x,
      baseTransform.position.y + delta,
      baseTransform.position.z
    )
    return result
  }

  if (intent === 'rotate_cw' || intent === 'rotate_ccw') {
    if (!allowRotation) {
      result.noOp = true
      result.reason = 'rotation_disabled_by_policy'
      return result
    }
    const sign = intent === 'rotate_cw' ? 1 : -1
    const delta = sign * rotationDelta
    result.totalDelta.rotationDeg[rotationAxis] = delta
    result.suggestedEndTransform.rotationDeg = makeRotationDeg(
      baseTransform.rotationDeg.x,
      baseTransform.rotationDeg.y,
      baseTransform.rotationDeg.z
    )
    result.suggestedEndTransform.rotationDeg[rotationAxis] =
      baseTransform.rotationDeg[rotationAxis] + delta
    return result
  }

  result.noOp = true
  result.reason = 'unsupported_intent'
  return result
}

export const resolveTransformMotionPreview = ({
  task = {},
  frameSpan = 1,
  fps = 24,
  currentTransform = null,
  transformMode = '2d',
  policy = {},
} = {}) =>
  resolveTransformSpeed({
    intent: task.intent,
    speedProfile: task.speedProfile,
    intensity: task.intensity,
    frameSpan,
    fps,
    currentTransform,
    transformMode,
    framingPolicy: policy.framingPolicy,
    allowBorderExposure: !!policy.allowBorderExposure,
    safeScaleFloorPct: finite(
      policy.safeScaleFloorPct,
      DEFAULT_SAFE_SCALE_FLOOR_PCT
    ),
    allowRotation:
      typeof policy.allowRotation === 'boolean' ? policy.allowRotation : true,
  })
